'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { useNotification } from '../../ui/components';
import { Buttons, Dialog, Layout } from '../../ui/material';
import { TextRegular } from '../../ui/text-components';
import { invalidateSentrySessionCache } from '../cache';
import { signOut, useSession } from '../session';
import { getIdleSessionConfig } from './config';
import { useIdleTimeout } from './useIdleTimeout';
export type SessionTimeoutManagerProps = {
  /** Override the idle window (ms). Defaults to env/config value. */
  idleTimeoutMs?: number;
  /** Override the warning countdown (ms). Defaults to env/config value. */
  warningCountdownMs?: number;
  /** Force-enable/disable. Defaults to config + authenticated session. */
  enabled?: boolean;
  /** Where to redirect after an idle sign-out. Defaults to the app origin. */
  redirectTo?: string;
};

/** Delay before redirect so the user can read the "signed out" toast. */
const SIGN_OUT_DELAY_MS = 2500;
function formatCountdown(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Idle-session guard for sensitive applications.
 *
 * After a configurable period of inactivity it prompts the user to stay signed
 * in; if they don't respond before the countdown ends, it signs them out
 * automatically. Only active for an authenticated session.
 */
export function SessionTimeoutManager(t0) {
  const $ = _c(35);
  const {
    idleTimeoutMs,
    warningCountdownMs,
    enabled,
    redirectTo
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getIdleSessionConfig();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const config = t1;
  const {
    status,
    refetch: update
  } = useSession();
  const {
    enqueueSnackbar
  } = useNotification();
  const isEnabled = (enabled ?? config.enabled) && status === "authenticated";
  const resolvedRedirect = redirectTo ?? (typeof window !== "undefined" ? window.location.origin : "/");
  let t2;
  if ($[1] !== enqueueSnackbar || $[2] !== resolvedRedirect) {
    t2 = () => {
      enqueueSnackbar("You were signed out due to inactivity. Please sign in again.", {
        variant: "warning",
        toastId: "session-idle-timeout",
        autoClose: SIGN_OUT_DELAY_MS
      });
      invalidateSentrySessionCache();
      window.setTimeout(() => {
        signOut({
          redirectTo: resolvedRedirect
        });
      }, SIGN_OUT_DELAY_MS);
    };
    $[1] = enqueueSnackbar;
    $[2] = resolvedRedirect;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleIdleTimeout = t2;
  const t3 = idleTimeoutMs ?? config.idleTimeoutMs;
  const t4 = warningCountdownMs ?? config.warningCountdownMs;
  let t5;
  if ($[4] !== handleIdleTimeout || $[5] !== isEnabled || $[6] !== t3 || $[7] !== t4) {
    t5 = {
      idleTimeoutMs: t3,
      warningCountdownMs: t4,
      enabled: isEnabled,
      onIdleTimeout: handleIdleTimeout
    };
    $[4] = handleIdleTimeout;
    $[5] = isEnabled;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const {
    isPrompted,
    remainingMs,
    stayActive
  } = useIdleTimeout(t5);
  let t6;
  if ($[9] !== stayActive || $[10] !== update) {
    t6 = () => {
      stayActive();
      update();
    };
    $[9] = stayActive;
    $[10] = update;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleStayActive = t6;
  let t7;
  if ($[12] !== resolvedRedirect) {
    t7 = () => {
      invalidateSentrySessionCache();
      signOut({
        redirectTo: resolvedRedirect
      });
    };
    $[12] = resolvedRedirect;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const handleSignOutNow = t7;
  if (!isEnabled || !isPrompted) {
    return null;
  }
  let t8;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      gap: 1.5
    };
    t9 = <TextRegular>For your security, you'll be signed out automatically due to inactivity in:</TextRegular>;
    $[14] = t8;
    $[15] = t9;
  } else {
    t8 = $[14];
    t9 = $[15];
  }
  let t10;
  if ($[16] !== remainingMs) {
    t10 = formatCountdown(remainingMs);
    $[16] = remainingMs;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10) {
    t11 = <TextRegular variant="title" weight="bold">{t10}</TextRegular>;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <TextRegular variant="muted">Choose "Stay signed in" to continue your session.</TextRegular>;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t11) {
    t13 = <Layout.BoxVertical sx={t8}>{t9}{t11}{t12}</Layout.BoxVertical>;
    $[21] = t11;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== handleSignOutNow) {
    t14 = <Buttons.SecondaryButton key="sign-out" onClick={handleSignOutNow} text="Sign out" />;
    $[23] = handleSignOutNow;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== handleStayActive) {
    t15 = <Buttons.PrimaryButton key="stay" onClick={handleStayActive} text="Stay signed in" />;
    $[25] = handleStayActive;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t14 || $[28] !== t15) {
    t16 = [t14, t15];
    $[27] = t14;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== handleStayActive || $[31] !== isPrompted || $[32] !== t13 || $[33] !== t16) {
    t17 = <Dialog open={isPrompted} onDialogClose={handleStayActive} showCloseIcon={false} maxWidth="xs" fullScreen={false} title="Are you still there?" content={t13} actions={t16} />;
    $[30] = handleStayActive;
    $[31] = isPrompted;
    $[32] = t13;
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  return t17;
}